"use client";
import { useSalesContact } from "@/components/site/sales-contact-context";

import { useRouter, useSearchParams } from "next/navigation";
import { useMemo, useState, type ReactNode } from "react";
import {
  CalendarClock,
  Check,
  ExternalLink,
  MapPin,
  Package,
} from "lucide-react";
import { useTranslations } from "next-intl";

import { Dialog, DialogContent, DialogTitleBar } from "@/components/ui/dialog";
import { CopyButton } from "@/components/ui/copy-button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatSession, mapHref } from "@/lib/shop/session-place";
import { PriceAmount, useSiteMoney } from "@/components/site/site-money";
import {
  axesFromSkus,
  findSkuBySpecs,
  normalizeSpecValues,
  orderSpecAxes,
  type SpecAxis,
  type SpecValues,
} from "@/lib/shop/sku-specs";
import {
  canPurchaseSku,
  initialShopSku,
  selectShopSpec,
  visibleSpecValues,
} from "@/lib/shop/sku-selection";
import type { SiteShopSku } from "@/templates/site/types";

export function ShopDetailHeader({
  cover,
  skus,
  specAxes,
  qrImage: defaultQrImage = "",
  qrCaption: defaultQrCaption = "",
  services = [],
  breadcrumb,
  heading,
}: {

  cover?: string;
  skus: SiteShopSku[];
  specAxes?: SpecAxis[];

  qrImage?: string;
  qrCaption?: string;

  services?: string[];
  breadcrumb: ReactNode;

  heading: ReactNode;
}) {
  const { qrImage, qrCaption } = useSalesContact({
    qrImage: defaultQrImage,
    qrCaption: defaultQrCaption,
  });
  const t = useTranslations("site.shop");
  const money = useSiteMoney();
  const router = useRouter();
  const searchParams = useSearchParams();

  const axes = useMemo(() => orderSpecAxes(axesFromSkus(skus), specAxes), [skus, specAxes]);
  const multiSpec = axes.length > 0;

  const [mountedAt] = useState(() => Date.now());
  const shared = Number(searchParams.get("spec"));
  const initial = initialShopSku(skus, shared, mountedAt);
  const [skuId, setSkuId] = useState(() => initial?.id ?? "");

  const [picked, setPicked] = useState<SpecValues>(() =>
    normalizeSpecValues(initial?.specs),
  );
  const [quantity, setQuantity] = useState(1);
  const [qrOpen, setQrOpen] = useState(false);

  const matched = multiSpec ? findSkuBySpecs(picked, axes, skus) : null;
  const selected = multiSpec
    ? matched
    : (skus.find((sku) => sku.id === skuId) ?? null);

  const sessionEnded = Boolean(
    selected?.endAt && new Date(selected.endAt).getTime() <= mountedAt,
  );

  const levelBlocked = Boolean(selected && !selected.levelAllowed);

  const outOfStock = !selected || selected.stock <= 0;
  const soldOut = outOfStock || sessionEnded || levelBlocked;

  const maxQuantity = Math.max(
    1,
    Math.min(
      selected?.stock ?? 1,
      selected?.purchaseLimit && selected.purchaseLimit > 0
        ? selected.purchaseLimit
        : Number.MAX_SAFE_INTEGER,
    ),
  );

  const image = selected?.thumb || cover || "";

  function hasStock(axisName: string, value: string, scoped: boolean): boolean {
    const index = axes.findIndex((axis) => axis.name === axisName);
    const constraints = scoped
      ? axes
          .slice(0, index)
          .filter((axis) => picked[axis.name])
          .map((axis) => [axis.name, picked[axis.name]])
      : [];

    return skus.some(
      (sku) =>
        sku.specs[axisName] === value &&
        sku.stock > 0 &&
        constraints.every(([name, wanted]) => sku.specs[name] === wanted),
    );
  }

  function syncUrl(sku: SiteShopSku | null) {
    const url = new URL(window.location.href);

    if (sku) {
      url.searchParams.set("spec", String(sku.serial));
    } else {
      url.searchParams.delete("spec");
    }

    window.history.replaceState(null, "", url);
  }

  function clampQuantity(stock: number) {

    setQuantity((current) => Math.min(current, Math.max(1, stock)));
  }

  function selectSku(next: SiteShopSku) {
    setSkuId(next.id);
    clampQuantity(next.stock);
    syncUrl(next);
  }

  function pickSpec(axisName: string, value: string) {
    const sku = selectShopSpec(axes, picked, axisName, value, skus, (row) =>
      canPurchaseSku(row, mountedAt),
    );
    if (!sku) return;
    setPicked(normalizeSpecValues(sku.specs));
    setSkuId(sku.id);
    clampQuantity(sku.stock);
    syncUrl(sku);
  }

  function handleBuyNow() {
    if (!selected) return;
    router.push(`/member/checkout/?skuId=${selected.id}&quantity=${quantity}`);
  }

  const facts: Array<{
    key: string;
    icon: ReactNode;

    label: string;
    value: string;
    warn?: string;

    href?: string;

    copyable?: boolean;
  }> = [];

  if (selected) {

    const isVirtual = selected.goodsType === "virtual";
    const session = isVirtual
      ? formatSession(selected.startAt, selected.endAt)
      : "";

    if (session) {
      facts.push({
        key: "session",
        icon: <CalendarClock aria-hidden />,
        label: t("sessionTime"),
        value: session,
        ...(sessionEnded ? { warn: t("sessionEnded") } : {}),
      });
    }
    if (isVirtual && selected.address) {
      facts.push({
        key: "address",
        icon: <MapPin aria-hidden />,
        label: t("sessionAddress"),
        value: selected.address,
        href: mapHref(
          selected.address,
          selected.lng,
          selected.lat,
          selected.geoProvider,
        ),
        copyable: true,
      });
    }

    facts.push({
      key: "stock",
      icon: <Package aria-hidden />,

      label: isVirtual ? t("seatsLabel") : t("stockLabel"),
      value: isVirtual
        ? t("remainSeats", { count: selected.stock })
        : t("remainStock", { count: selected.stock }),
      ...(selected.stock > 0 && selected.purchaseLimit > 0
        ? { warn: t("purchaseLimit", { count: selected.purchaseLimit }) }
        : {}),
    });
    if (levelBlocked) {
      facts.push({
        key: "level",
        icon: <Package aria-hidden />,
        label: t("levelLabel"),
        value: selected.minLevelName
          ? t("levelRequired", { level: selected.minLevelName })
          : t("levelRequiredHigher"),
      });
    }
  }

  const buyLabel = !selected
    ? t("pickSpecFirst")
    : sessionEnded
      ? t("sessionEnded")
      : outOfStock
        ? t("soldOut")
        : t("buyNow");

  return (
    <>
      <div className="kecheng_neirong_kuang_shang">
        <div className="mianbaoxie mianbaoxie_bjse">
          <div className="w1440 clearfix">{breadcrumb}</div>
        </div>

        <div className="kecheng_neirong_kuang_shang_zhengwen">
          <div className="w1440 flex-heng">
            {/* 没图就整块不渲染：.zuotu 是白底的图框，空着就是一大块白板 */}
            {image ? (
              <div className="zuotu">
                {/* 图来自内容库，地址不固定（本地 /uploads 或 OSS），不走 next/image */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={image} alt={selected?.specText || ""} />
              </div>
            ) : null}

            <div className={`youzi flex-shu${image ? "" : " youzi--full"}`}>
              <div className="youzi_shang">{heading}</div>

              {skus.length === 0 ? (
                <div className="goumai_ce goumai_ce_kong">{t("noSku")}</div>
              ) : (
                <div className="goumai_ce">
                  {facts.length > 0 ? (
                    <ul className="youzi_xia_er">
                      {facts.map((fact) => (
                        <li key={fact.key}>
                          {fact.icon}
                          {/* 标签不进链接：点的是地址本身，把「地点：」也划进可点区域
                              会让人以为标签也能点 */}
                          <span className="biaoqian">
                            {t("fieldLabel", { label: fact.label })}
                          </span>
                          {fact.href ? (
                            <a
                              className="ditu"
                              href={fact.href}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {fact.value}
                              <ExternalLink aria-hidden />
                            </a>
                          ) : (
                            <span>{fact.value}</span>
                          )}
                          {fact.copyable ? (
                            <CopyButton
                              className="fuzhi"
                              value={fact.value}
                              label={t("copyAddress")}
                              successText={t("addressCopied")}
                            />
                          ) : null}
                          {fact.warn ? <em>{fact.warn}</em> : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {/* 服务承诺排在场次信息下面、规格上面：它回答的是「买了有什么保障」，
                      和时间地点名额一样属于掏钱前要看的，摆到详情正文里就太靠后了。
                      形态照电商惯例——一行带勾的短标签，不占一整块 */}
                  {services.length > 0 ? (
                    <ul className="fuwu">
                      {services.map((item) => (
                        <li key={item}>
                          <Check aria-hidden />
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {multiSpec ? (

                    axes.map((axis) => (
                      <dl
                        key={axis.name}
                        className="youzi_pianjia youzi_dl flex-heng"
                      >
                        <dt>{t("fieldLabel", { label: axis.name })}</dt>
                        <dd>
                          {visibleSpecValues(axes, picked, axis.name, skus).map(
                            (value) => {
                              const active = picked[axis.name] === value;
                              const out = !hasStock(axis.name, value, false);

                              const dim = !hasStock(axis.name, value, true);

                              return (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() => pickSpec(axis.name, value)}
                                  className={`${active ? "on" : ""}${out ? " kucunwu" : dim && !active ? " an" : ""}`}
                                >
                                  {value}
                                </button>
                              );
                            },
                          )}
                        </dd>
                      </dl>
                    ))
                  ) : skus.length > 1 || skus[0]?.specText ? (

                    <dl className="youzi_pianjia youzi_dl flex-heng">
                      <dt>{t("fieldLabel", { label: t("spec") })}</dt>
                      <dd>
                        {skus.map((sku) => {
                          const out = sku.stock <= 0;
                          return (
                            <button
                              key={sku.id}
                              type="button"
                              onClick={() => selectSku(sku)}
                              className={`${sku.id === skuId ? "on" : ""}${out ? " kucunwu" : ""}`}
                            >
                              {sku.specText || t("standardSpec")}
                            </button>
                          );
                        })}
                      </dd>
                    </dl>
                  ) : null}

                  <dl className="youzi_goumaishu youzi_dl flex-heng">
                    <dt>{t("fieldLabel", { label: t("quantity") })}</dt>
                    <dd>
                      <button
                        type="button"
                        disabled={soldOut || quantity <= 1}
                        onClick={() =>
                          setQuantity((current) => Math.max(1, current - 1))
                        }
                        aria-label={t("decreaseQuantity")}
                      >
                        −
                      </button>
                      <span>{quantity}</span>
                      <button
                        type="button"
                        disabled={soldOut || quantity >= maxQuantity}
                        onClick={() =>
                          setQuantity((current) =>
                            Math.min(maxQuantity, current + 1),
                          )
                        }
                        aria-label={t("increaseQuantity")}
                      >
                        +
                      </button>
                    </dd>
                  </dl>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/*
        通栏购买条，照老站压在蓝色头部下沿。老站是拿脚本量出它的高度再取一半设成
        负 margin，蓝色区域的高度要等脚本跑完才定——刷新时能看到先铺一截再缩回去。
        这里压入量与上方留白都写死在 CSS 里，不需要任何测量。
      */}
      {skus.length > 0 ? (
        <div className="kecheng_neirong_kuang_goumai">
          <div className="w1440 beijingtu clearfix">
            <div className="flex-heng">
              <div className="kecheng_neirong_kuang_goumai_left">
                <div className="kecheng_neirong_kuang_goumai_jiage">
                  {selected ? (

                    <PriceAmount
                      amount={money.priceValue(selected.salePrice) * quantity}
                    />
                  ) : (
                    <span className="weixuan">{t("pickAllSpecs")}</span>
                  )}
                </div>
                {selected && selected.refPrice > selected.salePrice ? (
                  <div className="kecheng_neirong_kuang_goumai_yuanjia">
                    {money.format(
                      money.priceValue(selected.refPrice) * quantity,
                    )}
                  </div>
                ) : null}
              </div>

              <div className="kecheng_neirong_kuang_goumai_right flex-heng">
                {/* 老站这句是固定的营销话术，不是二维码下面那行说明，
                    所以不取 qrCaption */}
                <span className="kecheng_neirong_kuang_goumai_weixin">
                  {t("consultHint")}
                </span>
                {/* 二维码取后台「基础设置」里配的那张；没配就退回滚到页脚的联系方式，
                    别点了没反应 */}
                <a
                  className="kecheng_neirong_kuang_goumai_zixun"
                  href={qrImage ? "#" : "#footer-contact"}
                  onClick={(event) => {
                    if (!qrImage) return;
                    event.preventDefault();
                    setQrOpen(true);
                  }}
                >
                  {t("consult")}
                </a>
                <a
                  className="kecheng_neirong_kuang_goumai_xiadan"
                  href="#"
                  aria-disabled={soldOut}
                  onClick={(event) => {
                    event.preventDefault();
                    if (soldOut) return;
                    handleBuyNow();
                  }}
                >
                  {buyLabel}
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* 咨询二维码弹层与课程详情页共用同一套 Dialog */}
      <Dialog open={qrOpen && Boolean(qrImage)} onOpenChange={setQrOpen}>
        <DialogContent className="max-w-[min(20rem,calc(100%-30px))] gap-0 p-0">
          <DialogTitleBar title={t("consult")} />
          <div className="px-4 py-5">
            {/* 骨架垫在底层，图片盖在上面：图没到时这里透出骨架，到了自然遮住。
                不用 onLoad 切状态——图若命中缓存，挂载前就已经 complete，
                那个事件根本不会触发，图会一直停在透明状态上。 */}
            <div className="relative mx-auto aspect-square w-60 max-w-full">
              <Skeleton className="absolute inset-0 rounded-md" />
              {/* 二维码来自后台上传，地址不固定，不走 next/image */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={qrImage}
                alt={qrCaption || t("consult")}
                className="relative h-full w-full rounded-md bg-popover object-contain"
              />
            </div>
            <p className="mt-3 text-center text-sm text-muted-foreground">
              {qrCaption || t("consultHint")}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
